import { VbzInputText } from '@smiles/voebiz-ui-kit';
import { cpf } from 'cpf-cnpj-validator';
import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cpfMask } from '../../utils/maskCpf';
import { removeCpfMask } from '../../utils/removeCpfMask';

import { searchRegisterCPF } from '../../services/services';
import IncludeBeneficiaryForm from '../IncludeBeneficiaryForm';

interface Props {
  cpfValue: string;
  setCpfValue: React.Dispatch<React.SetStateAction<string>>;
  setErrorTypeIndividual: React.Dispatch<React.SetStateAction<string>>;
  setShowErrorModalIndividual: React.Dispatch<React.SetStateAction<boolean>>;
  setShowSuccessModalIndividual: React.Dispatch<React.SetStateAction<boolean>>;
  milesRange?: any;
  setShowTransactionFormIndividual: React.Dispatch<React.SetStateAction<boolean>>;
  showTransactionFormIndividual: boolean;
  modalType: 'team' | 'clients' | 'default';
}

const SearchCPFModal: React.FC<Props> = ({
  cpfValue,
  setCpfValue,
  setErrorTypeIndividual,
  setShowErrorModalIndividual,
  setShowSuccessModalIndividual,
  milesRange,
  showTransactionFormIndividual,
  setShowTransactionFormIndividual,
  modalType,
}) => {
  const { t } = useTranslation(['includeTeam', 'summary', 'modalInclude']);

  const [memberName, setMemberName] = useState('');
  const [cpfError, setCpfError] = useState(false);

  const handleCPFSearch = async () => {
    const isClient = modalType === 'clients';
    try {
      const cpfNoMask = removeCpfMask(cpfValue);
      const response = await searchRegisterCPF(cpfNoMask, isClient);
      if (response) {
        if (response.data.memberName.length > 0) {
          setMemberName(response.data.memberName);
          setShowTransactionFormIndividual(true);
        } else {
          setErrorTypeIndividual('cpfNotRegister');
          setShowErrorModalIndividual(true);
        }
      }
    } catch (error) {
      console.error('Erro na busca de CPF', error);
      setErrorTypeIndividual('cpfSearchError');
      setShowErrorModalIndividual(true);
    }
  };

  const validateCpfFormat = (e: ChangeEvent<HTMLInputElement>) => {
    const validated = cpf.isValid(e.target.value);
    const doc = e.target.value.replace(/\s/g, '');
    setCpfValue(doc);
    setCpfError(!validated);
  };

  const handleKeyDownNumber = (e: React.KeyboardEvent) => {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'];
    if (!(e.key >= '0' && e.key <= '9') && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleCleanFilter = () => {
    setCpfValue('');
    setCpfError(false);
  };

  const getModalTitle = () => {
    if (modalType === 'team') {
      return t('includeTeam:reward_team');
    }
    if (modalType === 'clients') {
      return t('includeTeam:reward_clients');
    }
    return 'Enviar milhas individualmente';
  };

  const getModalDescription = () => {
    if (modalType === 'clients') {
      return showTransactionFormIndividual
        ? t('includeTeam:form_clients_description')
        : t('includeTeam:reward_clients_description');
    }
    return t('includeTeam:reward_team_description');
  };

  return (
    <>
      <div className="include-one-search" data-testid="test-individual-search">
        <div className="include-one-search__description">
          <h4 className="include-one-search__description_title" data-testid="reward-title">
            {getModalTitle()}
          </h4>
          <p className="include-one-search__description_text" data-testid="reward-description">
            {getModalDescription()}
          </p>
        </div>
        <VbzInputText
          alt="beneficiario-cpf"
          placeholder="000.000.000-00"
          textLabel="CPF"
          inputMode="numeric"
          value={cpfMask(cpfValue)}
          forceError={cpfError}
          onKeyDown={(e) => {
            handleKeyDownNumber(e);
            if (e.key === 'Enter' && !cpfError && cpf.isValid(cpfValue)) {
              handleCPFSearch();
            }
          }}
          errorMessage="Informe um CPF válido."
          onChange={(e) => validateCpfFormat(e)}
          maxLength={14}
          icon={
            <>
              {cpfValue && (
                <button
                  type="button"
                  className="icon-clear-cpf"
                  data-testid="button-clear-search"
                  onClick={() => {
                    handleCleanFilter();
                  }}
                  aria-label="Clear CPF field"
                >
                  <i className="material-icons">close</i>
                </button>
              )}
              <button
                type="button"
                className="icon-search"
                data-testid="button-search"
                onClick={() => {
                  handleCPFSearch();
                }}
                aria-label="Search CPF"
              >
                <i className="material-icons">search</i>
              </button>
            </>
          }
        />
      </div>
      {showTransactionFormIndividual && (
        <IncludeBeneficiaryForm
          milesRange={milesRange}
          setCpfValue={setCpfValue}
          cpfValue={cpfValue}
          memberName={memberName}
          modalType={modalType}
          handleBackButtonClick={() => setShowTransactionFormIndividual(false)}
          setShowSuccessModalIndividual={setShowSuccessModalIndividual}
          setShowErrorModalIndividual={setShowErrorModalIndividual}
          setErrorTypeIndividual={setErrorTypeIndividual}
          setShowTransactionFormIndividual={setShowTransactionFormIndividual}
        />
      )}
    </>
  );
};

export default SearchCPFModal;
